import { I18n } from "react-redux-i18n";
import { getLocale, getSetting } from "../auth";

// User friendly texts of the entries of the menu change log (used by the change log page and by the history of the menu item/category editors).

// "Name (e-mail)", or the e-mail/name alone when only one of them is known
const joinNameAndEmail = (name, email) => {
  if (name && email && name !== email) {
    return name + " (" + email + ")";
  }
  return email || name || "";
};

// The user who did the modification (entry: modifiedBy is the e-mail address, modifierName the name)
export const formatModifier = entry => joinNameAndEmail(entry.modifierName, entry.modifiedBy);

// An option of the modifier filter: {value (e-mail), name}
export const formatModifierOption = modifier => joinNameAndEmail(modifier.name, modifier.value);

// The names of menu items and categories are stored as: hu="Vega Wrap";en="Vega Wrap";all="Vega Wrap" (or as a JSON text)
export const formatEntityName = name => {
  if (!name) {
    return "";
  }
  const raw = String(name);
  if (raw.charAt(0) === "{") {
    try {
      return getLocale(raw) || raw;
    } catch (e) {
      return raw;
    }
  }
  const names = {};
  const pattern = /([^=;"]+)="([^"]*)"/g;
  let match;
  let found = false;
  while ((match = pattern.exec(raw)) !== null) {
    names[match[1].trim().toLowerCase()] = match[2].trim();
    found = true;
  }
  if (!found) {
    return raw;
  }
  let language = "";
  let defaultLanguage = "";
  try {
    language = String(localStorage.language || "").toLowerCase();
    defaultLanguage = String(getSetting("default-language", "") || "").toLowerCase();
  } catch (e) {
    // the language settings are not available
  }
  const first = Object.keys(names).filter(key => names[key])[0];
  return names[language] || names.all || names[defaultLanguage] || (first ? names[first] : "") || raw;
};

const text = (key, fallback) => I18n.t("admin_local.menu_change_log." + key, { defaultValue: fallback });

// "vatCategoryId" -> "Vat category id"
const humanize = name => {
  const spaced = String(name)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.]+/g, " ")
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const fieldName = name => I18n.t("admin_local.menu_change_log_fields." + name, { defaultValue: humanize(name) });

// "item123/EXTRA" -> "#123, EXTRA"
const additionKey = key => {
  const match = String(key).match(/^(?:item|category)(\d+)\/(.*)$/);
  return match ? "#" + match[1] + (match[2] ? ", " + match[2] : "") : key;
};

export const formatChangeField = field => {
  const value = String(field);
  let match = value.match(/^availableQuantities\[(.+?)\]\.(.+)$/);
  if (match) {
    return fieldName(match[2]) + " (" + text("quantity", "quantity") + " " + match[1].replace(/#\d+$/, "") + ")";
  }
  match = value.match(/^menuItemAdditions\[(.+?)\]\.(.+)$/);
  if (match) {
    return fieldName(match[2]) + " (" + text("item_addition", "product addition") + " " + additionKey(match[1]) + ")";
  }
  match = value.match(/^menuCategoryAdditions\[(.+?)\]\.(.+)$/);
  if (match) {
    return fieldName(match[2]) + " (" + text("category_addition", "category addition") + " " + additionKey(match[1]) + ")";
  }
  match = value.match(/^names?\.(.+)$/);
  if (match) {
    return fieldName("name") + " (" + match[1] + ")";
  }
  match = value.match(/^availableQuantities\[(.+?)\]$/);
  if (match) {
    return text("quantity", "quantity") + " " + match[1];
  }
  return fieldName(value);
};

// side: "from" or "to"
// Nested objects (for example the channel settings of a quantity) as "foodpanda / foodora: on, price 2490; wolt / wolt: ..."
const formatObject = (object, path) => {
  const parts = [];
  Object.keys(object).sort().forEach(key => {
    const value = object[key];
    const name = path ? path + " / " + key : key;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const keys = Object.keys(value);
      const leaves = keys.filter(k => value[k] === null || typeof value[k] !== "object");
      if (keys.length === 0) {
        return;
      }
      if (leaves.length === keys.length) {
        const details = [];
        if (typeof value.enabled !== "undefined") {
          details.push(value.enabled === true || value.enabled === "true" ? text("on", "on") : text("off", "off"));
        }
        keys.filter(k => k !== "enabled" && value[k] !== "" && value[k] !== null).sort().forEach(k => {
          details.push(fieldName(k) + " " + (value[k] === true ? text("yes", "Yes") : value[k] === false ? text("no", "No") : value[k]));
        });
        if (details.length) {
          parts.push(name + ": " + details.join(", "));
        }
      } else {
        const nested = formatObject(value, name);
        if (nested) {
          parts.push(nested);
        }
      }
    } else if (value !== "" && value !== null && typeof value !== "undefined") {
      parts.push(name + ": " + (value === true ? text("yes", "Yes") : value === false ? text("no", "No") : Array.isArray(value) ? value.join(", ") : value));
    }
  });
  return parts.join("; ");
};

export const formatChangeValue = (change, side) => {
  const label = change[side + "Label"];
  if (label) {
    return label;
  }
  let value = change[side];
  if (typeof value === "string" && value.charAt(0) === "{") {
    try {
      value = JSON.parse(value);
    } catch (e) {
      // not a JSON text, shown as it is
    }
  }
  if (value === null || typeof value === "undefined" || value === "") {
    return "-";
  }
  if (value === true || value === "true") {
    return text("yes", "Yes");
  }
  if (value === false || value === "false") {
    return text("no", "No");
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }
  if (typeof value === "object") {
    return formatObject(value, "") || "-";
  }
  const stringValue = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(stringValue)) {
    const date = new Date(stringValue);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  }
  const list = stringValue.match(/^\[(.*)\]$/);
  if (list) {
    const items = list[1].split(",").map(item => item.trim()).filter(Boolean);
    return items.length ? items.join(", ") : "-";
  }
  return stringValue;
};

// "Available: Yes -> No; Price (quantity 0.5): 1200 -> 1300 (+2 more)"
export const summarizeChanges = (changes, max) => {
  const list = changes || [];
  const limit = max || 3;
  const parts = list.slice(0, limit).map(change => formatChangeField(change.field) + ": " + formatChangeValue(change, "from") + " \u2192 " + formatChangeValue(change, "to"));
  if (list.length > limit) {
    parts.push("+" + (list.length - limit) + " " + text("more", "more"));
  }
  return parts.join("; ");
};
